/**
 * Authentication Form Validation Utilities
 * Provides validation rules and helpers for Sign In and Sign Up forms.
 */

// Strict RFC-compliant email regex
// - Local part: allows standard alphanumeric and safe characters; no consecutive dots; cannot start/end with dot
// - Domain part: valid labels separated by single dots; cannot start/end with dot or hyphen; TLD >= 2 letters
export const EMAIL_REGEX = /^[a-zA-Z0-9]+([._%+-][a-zA-Z0-9]+)*@[a-zA-Z0-9]+([.-][a-zA-Z0-9]+)*\.[a-zA-Z]{2,}$/;

export const COMMON_TYPO_DOMAINS = {
	"gamil.com": "gmail.com",
	"gmaill.com": "gmail.com",
	"gmial.com": "gmail.com",
	"gmai.com": "gmail.com",
	"gamil.co": "gmail.com",
	"gmail.con": "gmail.com",
	"gmaill.co": "gmail.com",
	"gmail.co": "gmail.com",
	"gmail.cm": "gmail.com",
	"gmaik.com": "gmail.com",
	"gnail.com": "gmail.com",
	"gemail.com": "gmail.com",
	"gmaul.com": "gmail.com",
	"yaho.com": "yahoo.com",
	"yahooo.com": "yahoo.com",
	"yaho.co": "yahoo.com",
	"yahoo.con": "yahoo.com",
	"yaho.co.in": "yahoo.co.in",
	"yahooo.co.in": "yahoo.co.in",
	"yaho.in": "yahoo.co.in",
	"yahou.com": "yahoo.com",
	"hotmial.com": "hotmail.com",
	"hotmai.com": "hotmail.com",
	"hotmial.co": "hotmail.com",
	"hotmail.con": "hotmail.com",
	"hotmaill.com": "hotmail.com",
	"outlok.com": "outlook.com",
	"outloo.com": "outlook.com",
	"outlook.con": "outlook.com",
	"outlock.com": "outlook.com",
	"iclud.com": "icloud.com",
	"icld.com": "icloud.com",
	"icoud.com": "icloud.com",
};

export const BLOCKED_DOMAINS = new Set([
	// Test & example domains (RFC 2606 / 6761)
	"example.com",
	"example.org",
	"example.net",
	"example.edu",
	"test.com",
	"test.org",
	"test.net",
	"testing.com",
	"invalid.com",
	"localhost",
	"sample.com",
	// Dummy / placeholder domains
	"asdf.com",
	"fake.com",
	"fakemail.com",
	"dummy.com",
	"dummymail.com",
	"temp.com",
	"demo.com",
	"user.com",
	"xyz.com",
	"abc.com",
	"falsemail.com",
	"nowhere.com",
	"random.com",
	"foo.com",
	"bar.com",
	"baz.com",
	"nonexistent.com",
	// Disposable / temp email services
	"mailinator.com",
	"mailinator2.com",
	"tempmail.com",
	"temp-mail.org",
	"temp-mail.io",
	"10minutemail.com",
	"guerrillamail.com",
	"guerrillamail.net",
	"guerrillamail.org",
	"guerrillamail.biz",
	"guerrillamail.de",
	"guerrillamailblock.com",
	"throwawaymail.com",
	"yopmail.com",
	"yopmail.fr",
	"yopmail.net",
	"trashmail.com",
	"trashmail.net",
	"getnada.com",
	"dispostable.com",
	"sharklasers.com",
	"fakemailgenerator.com",
	"fakemail.net",
	"mohmal.com",
	"mohmal.in",
	"mytemp.email",
	"generator.email",
	"emailondeck.com",
	"crazymailing.com",
	"burnermail.io",
	"nada.ltd",
	"dropmail.me",
	"maildrop.cc",
	"inboxkitten.com",
	"tempail.com",
	"minuteinbox.com",
	"wmail.ml",
	"getairmail.com",
	"suresmail.info",
	"trbvm.com",
	"fakeinbox.com",
	"harakirimail.com",
	"mailcatch.com",
	"emailfake.com",
	"internxt.com",
	"mailnesia.com",
	"incognitomail.org",
	"emailtemp.org",
]);

export const PLACEHOLDER_USERNAMES = new Set([
	"test",
	"testing",
	"tester",
	"test1234",
	"fake",
	"fakemail",
	"fakeuser",
	"dummy",
	"dummyuser",
	"dummyemail",
	"sample",
	"sampleuser",
	"sampleemail",
	"asdf",
	"asdfgh",
	"asdfasdf",
	"qwerty",
	"qwertyuiop",
	"admin",
	"administrator",
	"nonexistent",
	"nobody",
	"noone",
]);

export const validateProviderRules = (localPart, domain) => {
	if (domain === "gmail.com" || domain === "googlemail.com") {
		const baseUsername = localPart.split("+")[0];
		if (baseUsername.length < 6) {
			return "Gmail usernames must be at least 6 characters long";
		}
		if (baseUsername.length > 30) {
			return "Gmail usernames cannot exceed 30 characters";
		}
		if (!/^[a-zA-Z0-9.]+$/.test(baseUsername)) {
			return "Gmail usernames can only contain letters, numbers, and periods";
		}
		if (baseUsername.startsWith(".") || baseUsername.endsWith(".")) {
			return "Gmail username cannot start or end with a period";
		}
	}

	if (domain.startsWith("yahoo.") || domain === "ymail.com" || domain === "rocketmail.com") {
		const baseUsername = localPart.split("+")[0];
		if (baseUsername.length < 4 || baseUsername.length > 32) {
			return "Yahoo email username must be between 4 and 32 characters";
		}
		if (!/^[a-zA-Z]/.test(baseUsername)) {
			return "Yahoo email username must start with a letter";
		}
		if (!/^[a-zA-Z0-9._]+$/.test(baseUsername)) {
			return "Yahoo email addresses can only contain letters, numbers, periods, and underscores";
		}
	}

	if (
		domain === "outlook.com" ||
		domain === "hotmail.com" ||
		domain === "live.com" ||
		domain === "msn.com"
	) {
		const baseUsername = localPart.split("+")[0];
		if (baseUsername.length < 3 || baseUsername.length > 64) {
			return "Outlook/Hotmail email username must be between 3 and 64 characters";
		}
		if (!/^[a-zA-Z]/.test(baseUsername)) {
			return "Outlook/Hotmail email address must start with a letter";
		}
	}

	return "";
};

/**
 * Returns formatted YYYY-MM-DD string for the maximum allowable birthdate (13 years ago from today)
 */
export const getMaxDobDate = () => {
	const date = new Date();
	date.setFullYear(date.getFullYear() - 13);
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

/**
 * Validate email address
 */
export const validateEmail = (email) => {
	if (!email || !email.trim()) {
		return "Email address is required";
	}
	const trimmed = email.trim().toLowerCase();
	if (trimmed.length > 254) {
		return "Email must not exceed 254 characters";
	}

	const atParts = trimmed.split("@");
	if (atParts.length !== 2) {
		return "Please enter a valid email address with a single '@'";
	}

	const [localPart, domainPart] = atParts;
	if (!localPart) {
		return "Email username before '@' cannot be empty";
	}
	if (localPart.length > 64) {
		return "Email username must not exceed 64 characters";
	}
	if (localPart.startsWith(".") || localPart.endsWith(".")) {
		return "Email username cannot start or end with a dot ('.')";
	}
	if (localPart.includes("..")) {
		return "Email cannot contain consecutive dots ('..')";
	}

	if (!domainPart) {
		return "Email domain after '@' cannot be empty";
	}
	if (domainPart.length > 253) {
		return "Email domain must not exceed 253 characters";
	}
	if (domainPart.startsWith(".") || domainPart.endsWith(".")) {
		return "Email domain cannot start or end with a dot ('.')";
	}
	if (domainPart.includes("..")) {
		return "Email domain cannot contain consecutive dots ('..')";
	}

	if (!EMAIL_REGEX.test(trimmed)) {
		return "Please enter a valid email address (e.g. name@example.com)";
	}

	const domainLabels = domainPart.split(".");
	if (domainLabels.length < 2) {
		return "Email domain must include an extension (e.g. .com)";
	}

	for (const label of domainLabels) {
		if (!label) {
			return "Invalid email domain format";
		}
		if (label.startsWith("-") || label.endsWith("-")) {
			return "Email domain labels cannot start or end with a hyphen";
		}
	}

	const tld = domainLabels[domainLabels.length - 1];
	if (!/^[a-zA-Z]{2,}$/.test(tld)) {
		return "Email domain extension must contain at least 2 letters";
	}

	// Check placeholder usernames
	const baseUser = localPart.split("+")[0].toLowerCase();
	if (PLACEHOLDER_USERNAMES.has(baseUser)) {
		return "Please enter your actual valid personal email address";
	}

	// Check provider-specific username rules
	const providerError = validateProviderRules(localPart, domainPart);
	if (providerError) {
		return providerError;
	}

	// Check common typos
	if (COMMON_TYPO_DOMAINS[domainPart]) {
		const suggestion = COMMON_TYPO_DOMAINS[domainPart];
		return `Invalid email domain. Did you mean @${suggestion}?`;
	}

	// Check blocked / disposable / test domains
	if (BLOCKED_DOMAINS.has(domainPart)) {
		return "Disposable, placeholder, or test email addresses are not allowed";
	}

	return "";
};

/**
 * Validate username / name
 */
export const validateName = (name) => {
	if (!name || !name.trim()) {
		return "Name is required";
	}
	const trimmed = name.trim();
	if (trimmed.length < 3) {
		return "Name must be at least 3 characters";
	}
	if (trimmed.length > 50) {
		return "Name must be 50 characters or less";
	}
	// Disallow inputs made only of special characters or pure numbers
	if (!/[a-zA-Z]/.test(trimmed)) {
		return "Name must contain at least one letter";
	}
	return "";
};

/**
 * Get individual password criteria status for real-time visual checklist
 */
export const getPasswordCriteria = (password = "") => {
	return {
		minLength: password.length >= 8,
		hasUppercase: /[A-Z]/.test(password),
		hasNumber: /\d/.test(password),
		hasSpecialChar: /[!@#$%^&*()_+{}\[\]:;<>,.?~\\/-]/.test(password),
	};
};

/**
 * Validate password rules for sign up
 */
export const validatePassword = (password) => {
	if (!password) {
		return "Password is required";
	}
	if (password.length < 8) {
		return "Password must be at least 8 characters";
	}
	if (!/[A-Z]/.test(password)) {
		return "Password must include at least one uppercase letter";
	}
	if (!/\d/.test(password)) {
		return "Password must include at least one number";
	}
	if (!/[!@#$%^&*()_+{}\[\]:;<>,.?~\\/-]/.test(password)) {
		return "Password must include at least one special character";
	}
	return "";
};

/**
 * Validate password for sign in
 */
export const validateSignInPassword = (password) => {
	if (!password) {
		return "Password is required";
	}
	if (password.length < 8) {
		return "Password must be at least 8 characters";
	}
	return "";
};

/**
 * Validate confirm password
 */
export const validateConfirmPassword = (password, confirmPassword) => {
	if (!confirmPassword) {
		return "Please confirm your password";
	}
	if (password !== confirmPassword) {
		return "Passwords do not match";
	}
	return "";
};

/**
 * Validate Date of Birth
 */
export const validateDob = (dob) => {
	if (!dob) {
		return "Date of birth is required";
	}
	const birthDate = new Date(dob);
	if (isNaN(birthDate.getTime())) {
		return "Please select a valid date";
	}
	const today = new Date();
	if (birthDate > today) {
		return "Date of birth cannot be in the future";
	}

	// Calculate age in years
	let age = today.getFullYear() - birthDate.getFullYear();
	const monthDiff = today.getMonth() - birthDate.getMonth();
	if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
		age--;
	}

	if (age < 13) {
		return "You must be at least 13 years old";
	}
	if (age > 120) {
		return "Please enter a valid date of birth";
	}

	return "";
};

/**
 * Validate Gender
 */
export const validateGender = (gender) => {
	if (!gender || !gender.trim()) {
		return "Please select your gender";
	}
	const normalized = gender.trim().toLowerCase();
	if (normalized !== "male" && normalized !== "female") {
		return "Please select a valid gender";
	}
	return "";
};

/**
 * Validate Sign In form data
 */
export const validateSignIn = ({ email, password }) => {
	const errors = {};
	const emailError = validateEmail(email);
	if (emailError) errors.email = emailError;

	const passwordError = validateSignInPassword(password);
	if (passwordError) errors.password = passwordError;

	return {
		isValid: Object.keys(errors).length === 0,
		errors,
	};
};

/**
 * Validate Sign Up form data
 */
export const validateSignUp = ({ name, email, gender, dob, password, conPassWord }) => {
	const errors = {};

	const nameError = validateName(name);
	if (nameError) errors.name = nameError;

	const emailError = validateEmail(email);
	if (emailError) errors.email = emailError;

	const genderError = validateGender(gender);
	if (genderError) errors.gender = genderError;

	const dobError = validateDob(dob);
	if (dobError) errors.dob = dobError;

	const passwordError = validatePassword(password);
	if (passwordError) errors.password = passwordError;

	const conPassError = validateConfirmPassword(password, conPassWord);
	if (conPassError) errors.conPassWord = conPassError;

	return {
		isValid: Object.keys(errors).length === 0,
		errors,
	};
};
